import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search, RefreshCw, AlertCircle, History as HistoryIcon, ChevronDown, ChevronRight } from 'lucide-react';
import { useLanguage } from '../../contexts/LanguageContext';
import useAuth from '../../hooks/useAuth';

const BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000/api';
const PAGE_SIZE = 50;

const ACTION_STYLES = {
  created: 'bg-green-100 text-green-700',
  updated: 'bg-blue-100 text-blue-700',
  deleted: 'bg-red-100 text-red-700',
};

function authHeaders() {
  const token = localStorage.getItem('auth_token');
  return token ? { Authorization: `Bearer ${token}` } : {};
}

function fmtVal(v) {
  if (v === null || v === undefined || v === '') return '—';
  const s = String(v);
  return s.length <= 200 ? s : s.slice(0, 197) + '…';
}

function ChangesTable({ changes }) {
  const entries = Object.entries(changes || {});
  if (entries.length === 0) return <span className="text-gray-400">—</span>;
  return (
    <table className="w-full text-xs border border-gray-200 rounded">
      <thead>
        <tr className="bg-gray-50 text-gray-500">
          <th className="text-left px-3 py-1.5 font-semibold">Field</th>
          <th className="text-left px-3 py-1.5 font-semibold">Old</th>
          <th className="text-left px-3 py-1.5 font-semibold">New</th>
        </tr>
      </thead>
      <tbody>
        {entries.map(([field, pair]) => {
          const [old, nw] = Array.isArray(pair) && pair.length === 2 ? pair : [null, pair];
          return (
            <tr key={field} className="border-t border-gray-100 align-top">
              <td className="px-3 py-1.5 font-medium text-gray-700">{field}</td>
              <td className="px-3 py-1.5 text-red-600 break-all">{fmtVal(old)}</td>
              <td className="px-3 py-1.5 text-green-600 break-all">{fmtVal(nw)}</td>
            </tr>
          );
        })}
      </tbody>
    </table>
  );
}

export default function AdminHistory() {
  const { t } = useLanguage();
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();

  const [rows, setRows]     = useState([]);
  const [count, setCount]   = useState(0);
  const [page, setPage]     = useState(1);
  const [models, setModels] = useState([]);
  const [modelFilter, setModelFilter]   = useState('');
  const [actionFilter, setActionFilter] = useState('');
  const [search, setSearch] = useState('');
  const [query, setQuery]   = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError]   = useState('');
  const [expanded, setExpanded] = useState(null);

  const fullAdmin = !!user && ['admin', 'superadmin'].includes(user.role) && user.is_staff && user.is_superuser;

  // Full admins only — the backend 403s anyway, but redirect for good measure.
  useEffect(() => {
    if (!authLoading && user && !fullAdmin) navigate('/admin-panel/dashboard', { replace: true });
  }, [authLoading, user, fullAdmin, navigate]);

  const fetchModels = useCallback(async () => {
    try {
      const res = await fetch(`${BASE_URL}/audit/models/`, { headers: authHeaders() });
      if (res.ok) { const d = await res.json(); setModels(d.models || []); }
    } catch {}
  }, []);

  const fetchLogs = useCallback(async () => {
    setLoading(true); setError('');
    try {
      const params = new URLSearchParams({ page: String(page), page_size: String(PAGE_SIZE) });
      if (modelFilter)  params.set('model_name', modelFilter);
      if (actionFilter) params.set('action', actionFilter);
      if (query)        params.set('search', query);
      const res = await fetch(`${BASE_URL}/audit/logs/?${params}`, { headers: authHeaders() });
      if (!res.ok) throw new Error();
      const d = await res.json();
      setRows(d.results || []);
      setCount(d.count || 0);
    } catch { setError('Failed to load history.'); }
    finally { setLoading(false); }
  }, [page, modelFilter, actionFilter, query]);

  useEffect(() => { if (fullAdmin) fetchModels(); }, [fullAdmin, fetchModels]);
  useEffect(() => { if (fullAdmin) fetchLogs(); }, [fullAdmin, fetchLogs]);

  const totalPages = Math.max(1, Math.ceil(count / PAGE_SIZE));

  if (!fullAdmin) return null;

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h1 className="text-xl font-bold text-gray-800 flex items-center gap-2">
          <HistoryIcon size={20} /> {t('history')}
        </h1>
        <button onClick={fetchLogs} disabled={loading}
          className="px-3 py-1.5 bg-primary-50 text-primary-700 rounded-md hover:bg-primary-100 flex items-center text-sm">
          <RefreshCw className={`h-4 w-4 mr-1 ${loading ? 'animate-spin' : ''}`} />
          {t('refresh')}
        </button>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap gap-3">
        <select value={modelFilter} onChange={e => { setModelFilter(e.target.value); setPage(1); }}
          className="text-sm border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary-500">
          <option value="">{t('allModels')}</option>
          {models.map(m => <option key={m} value={m}>{m}</option>)}
        </select>
        <select value={actionFilter} onChange={e => { setActionFilter(e.target.value); setPage(1); }}
          className="text-sm border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary-500">
          <option value="">{t('allActions')}</option>
          <option value="created">Created</option>
          <option value="updated">Updated</option>
          <option value="deleted">Deleted</option>
        </select>
        <form onSubmit={e => { e.preventDefault(); setPage(1); setQuery(search); }} className="flex gap-2">
          <div className="relative">
            <Search size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400" />
            <input type="text" placeholder="Search object / user…" value={search} onChange={e => setSearch(e.target.value)}
              className="pl-8 pr-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500" />
          </div>
          <button type="submit" className="px-3 py-2 text-sm bg-primary-600 text-white rounded-md hover:bg-primary-700">Search</button>
        </form>
      </div>

      {error && (
        <div className="flex items-center gap-2 text-red-600 bg-red-50 border border-red-200 rounded-md px-4 py-3 text-sm">
          <AlertCircle size={16} />{error}
        </div>
      )}

      <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                {['', t('date'), t('user'), t('action'), t('model'), t('object'), t('changes')].map((h, i) => (
                  <th key={i} className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide whitespace-nowrap">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {loading ? [...Array(8)].map((_, i) => (
                <tr key={i}>{[...Array(7)].map((_, j) => <td key={j} className="px-4 py-3"><div className="h-4 bg-gray-100 rounded animate-pulse" /></td>)}</tr>
              )) : rows.length === 0 ? (
                <tr><td colSpan={7} className="text-center py-10 text-gray-400">{t('noHistory')}</td></tr>
              ) : rows.map(r => {
                const hasChanges = r.changes && Object.keys(r.changes).length > 0;
                const open = expanded === r.id;
                return (
                  <React.Fragment key={r.id}>
                    <tr className={`hover:bg-primary-50 ${hasChanges ? 'cursor-pointer' : ''}`}
                      onClick={() => hasChanges && setExpanded(open ? null : r.id)}>
                      <td className="px-4 py-3 text-gray-400 w-6">
                        {hasChanges ? (open ? <ChevronDown size={16} /> : <ChevronRight size={16} />) : null}
                      </td>
                      <td className="px-4 py-3 text-gray-600 whitespace-nowrap">{new Date(r.timestamp).toLocaleString()}</td>
                      <td className="px-4 py-3 text-gray-700 whitespace-nowrap">{r.user_display || '—'}</td>
                      <td className="px-4 py-3">
                        <span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${ACTION_STYLES[r.action] || 'bg-gray-100 text-gray-600'}`}>
                          {r.action.toUpperCase()}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-gray-600 whitespace-nowrap">{r.model_name}</td>
                      <td className="px-4 py-3 text-gray-700 max-w-xs truncate" title={r.object_repr}>
                        {r.object_repr || `#${r.object_id}`}
                      </td>
                      <td className="px-4 py-3 text-gray-400 text-xs whitespace-nowrap">
                        {hasChanges ? `${Object.keys(r.changes).length} field(s)` : '—'}
                      </td>
                    </tr>
                    {open && hasChanges && (
                      <tr className="bg-gray-50">
                        <td colSpan={7} className="px-4 py-3"><ChangesTable changes={r.changes} /></td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        </div>

        {!loading && count > 0 && (
          <div className="px-4 py-3 border-t border-gray-200 flex items-center justify-between">
            <span className="text-xs text-gray-500">
              {(page - 1) * PAGE_SIZE + 1}–{Math.min(page * PAGE_SIZE, count)} / {count}
            </span>
            <div className="flex items-center gap-1">
              <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1}
                className="px-2 py-1 text-xs rounded border border-gray-300 disabled:opacity-40 hover:bg-gray-50">‹</button>
              <span className="text-xs text-gray-500 px-2">{page} / {totalPages}</span>
              <button onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={page >= totalPages}
                className="px-2 py-1 text-xs rounded border border-gray-300 disabled:opacity-40 hover:bg-gray-50">›</button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
